'use client'

import { useState, useEffect } from 'react'
import { getRepositories } from '@/app/actions/github'
import { createProject, createProjectFromTemplate, getBaseDomain } from '@/app/actions/projects'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useRouter } from 'next/navigation'
import { Plus, GitBranch, Sparkles, Loader2, AlertCircle, Settings, Server, FolderOpen, Zap } from 'lucide-react'
import Link from 'next/link'

export function AddProjectDialog() {
    const [open, setOpen] = useState(false)
    const router = useRouter()

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button>
                    <Plus className="mr-2 h-4 w-4" /> Add Project
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Add Project</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="create" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="create" className="gap-2">
                            <Sparkles className="h-4 w-4" />
                            Create New
                        </TabsTrigger>
                        <TabsTrigger value="connect" className="gap-2">
                            <GitBranch className="h-4 w-4" />
                            Connect GitHub
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="create" className="mt-4">
                        <CreateNewForm onSuccess={() => { setOpen(false); router.refresh() }} />
                    </TabsContent>
                    <TabsContent value="connect" className="mt-4">
                        <ConnectExistingForm onSuccess={() => { setOpen(false); router.refresh() }} />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}

function ConnectExistingForm({ onSuccess }: { onSuccess: () => void }) {
    const [repos, setRepos] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [selectedRepo, setSelectedRepo] = useState<string>('')
    const [branch, setBranch] = useState('main')
    const [error, setError] = useState('')
    const [loadError, setLoadError] = useState<string | null>(null)
    const [isAuthError, setIsAuthError] = useState(false)

    useEffect(() => {
        async function fetchRepos() {
            setLoadError(null)
            setIsAuthError(false)
            try {
                const result = await getRepositories()
                if (result.success) {
                    setRepos(result.data || [])
                } else {
                    setLoadError(result.error || 'Failed to load repositories')
                    // Check if it's an auth-related error
                    const errorLower = (result.error || '').toLowerCase()
                    if (errorLower.includes('token') || errorLower.includes('expired') ||
                        errorLower.includes('invalid') || errorLower.includes('401') ||
                        errorLower.includes('unauthorized') || errorLower.includes('re-link')) {
                        setIsAuthError(true)
                    }
                }
            } catch (error) {
                console.error('Failed to fetch repos', error)
                setLoadError(error instanceof Error ? error.message : 'Failed to load repositories')
            } finally {
                setLoading(false)
            }
        }
        fetchRepos()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!selectedRepo) return

        const repo = repos.find(r => r.url === selectedRepo)

        setSubmitting(true)
        setError('')
        try {
            const result = await createProject({
                name: repo.name,
                repoUrl: repo.url,
                branch,
                envVars: ''
            })
            if (result.success) {
                onSuccess()
            } else {
                setError(result.error)
            }
        } catch (err) {
            console.error('Failed to create project', err)
            setError(err instanceof Error ? err.message : 'Failed to create project')
        } finally {
            setSubmitting(false)
        }
    }

    // Show error state when loading fails
    if (loadError) {
        return (
            <div className="space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-red-600 dark:text-red-400">
                                Failed to load repositories
                            </p>
                            <p className="text-sm text-red-600/80 dark:text-red-400/80">
                                {loadError}
                            </p>
                            {isAuthError && (
                                <Link
                                    href="/?tab=settings"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
                                >
                                    <Settings className="h-4 w-4" />
                                    Go to Settings to fix GitHub connection
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Select a repository from your GitHub account to connect.
            </p>
            <div className="space-y-2">
                <Label htmlFor="repo">Repository</Label>
                <Select onValueChange={setSelectedRepo} value={selectedRepo}>
                    <SelectTrigger>
                        <SelectValue placeholder={loading ? "Loading repositories..." : "Select a repository"} />
                    </SelectTrigger>
                    <SelectContent>
                        {repos.map((repo) => (
                            <SelectItem key={repo.id} value={repo.url}>
                                {repo.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2">
                <Label htmlFor="branch">Branch</Label>
                <Input
                    id="branch"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    placeholder="main"
                />
            </div>

            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}

            <Button type="submit" disabled={!selectedRepo || submitting} className="w-full">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect Project
            </Button>
        </form>
    )
}

function CreateNewForm({ onSuccess }: { onSuccess: () => void }) {
    const [name, setName] = useState('')
    const [template, setTemplate] = useState<'empty' | 'go-server' | 'bun-server'>('bun-server')
    const [domain, setDomain] = useState('')
    const [baseDomain, setBaseDomain] = useState('fanruten.com')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    // Load base domain on mount
    useEffect(() => {
        getBaseDomain().then(setBaseDomain).catch(() => {})
    }, [])

    // Sanitize name (auto-lowercase)
    const sanitizedName = name.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')

    // Auto-generate domain when name changes (for server templates)
    const generatedDomain = sanitizedName ? `${sanitizedName}.${baseDomain}` : ''

    // Templates that need domain
    const needsDomain = template === 'go-server' || template === 'bun-server'

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!sanitizedName) return
        if (needsDomain && !domain && !generatedDomain) return

        setSubmitting(true)
        setError('')
        try {
            const result = await createProjectFromTemplate({
                name: sanitizedName,
                template,
                domain: needsDomain ? (domain || generatedDomain) : undefined,
            })
            if (result.success) {
                onSuccess()
            } else {
                setError(result.error)
            }
        } catch (err) {
            console.error('Failed to create project', err)
            setError(err instanceof Error ? err.message : 'Failed to create project')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
                <Label>Template</Label>
                <RadioGroup value={template} onValueChange={(v) => setTemplate(v as 'empty' | 'go-server' | 'bun-server')} className="grid grid-cols-3 gap-2">
                    <Label
                        htmlFor="template-bun"
                        className={`flex flex-col items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${template === 'bun-server' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'}`}
                    >
                        <RadioGroupItem value="bun-server" id="template-bun" className="sr-only" />
                        <Zap className="h-5 w-5 text-muted-foreground" />
                        <div className="text-center">
                            <p className="font-medium text-sm">Bun</p>
                            <p className="text-xs text-muted-foreground">Fast & simple</p>
                        </div>
                    </Label>
                    <Label
                        htmlFor="template-go"
                        className={`flex flex-col items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${template === 'go-server' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'}`}
                    >
                        <RadioGroupItem value="go-server" id="template-go" className="sr-only" />
                        <Server className="h-5 w-5 text-muted-foreground" />
                        <div className="text-center">
                            <p className="font-medium text-sm">Go</p>
                            <p className="text-xs text-muted-foreground">Compiled</p>
                        </div>
                    </Label>
                    <Label
                        htmlFor="template-empty"
                        className={`flex flex-col items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${template === 'empty' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50'}`}
                    >
                        <RadioGroupItem value="empty" id="template-empty" className="sr-only" />
                        <FolderOpen className="h-5 w-5 text-muted-foreground" />
                        <div className="text-center">
                            <p className="font-medium text-sm">Empty</p>
                            <p className="text-xs text-muted-foreground">DIY</p>
                        </div>
                    </Label>
                </RadioGroup>
            </div>

            <div className="space-y-2">
                <Label htmlFor="name">Project Name</Label>
                <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="my-awesome-project"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                />
                {name && sanitizedName !== name.toLowerCase() && (
                    <p className="text-xs text-muted-foreground">
                        Will be created as: <code className="bg-muted px-1 py-0.5 rounded">{sanitizedName}</code>
                    </p>
                )}
            </div>

            {needsDomain && (
                <div className="space-y-2">
                    <Label htmlFor="domain">Domain <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                        id="domain"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder={generatedDomain || `myapp.${baseDomain}`}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                    <p className="text-xs text-muted-foreground">
                        {domain ? 'Custom domain' : `Default: ${generatedDomain || `<name>.${baseDomain}`}`}
                    </p>
                </div>
            )}

            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}

            <Button type="submit" disabled={!sanitizedName || submitting} className="w-full">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Project
            </Button>
        </form>
    )
}

